/* The control panel's own styling. Everything themeable resolves through a
   `--mondrian-*` token from `mondrian-tokens.css`, so a host theme switch
   re-paints this file without it knowing.

   ⚠️ This is BUNDLED INTO app.js and injected as a <style> element, not served
   as a stylesheet. That is a CSP decision, not a build convenience: the page's
   policy is `default-src 'none'` with `style-src 'unsafe-inline'`, which covers
   a script-inserted <style> and would NOT cover a <link> without adding `'self'`
   to style-src. Bundling also means the hosted Worker page and the dev server's
   page — two hand-written copies of the same HTML — cannot drift in how the
   panel looks, because neither of them describes it. */

/* ── Type ─────────────────────────────────────────────────────────────────────
   Giraffe's panel type is TWO sizes and TWO weights, measured from staging on
   2026-09-04 (74 visible text leaves, 8 distinct styles, three of them
   Mapbox's): 12px/550 section header, 11px/550 active item, 11px/400 everything
   else. No uppercase, no letter-spacing — the product's only tracked type is in
   dropdown and command-palette group headings. Its own CSS carries
   `body{font-size:11px}`.

   Inter is the product's only in-app typeface, and the host's copy does NOT
   cross into the frame (multifamily measured the frame computing Times). It is
   named here anyway and allowed to fall back: loading it from Google Fonts
   would mean adding `font-src https://fonts.gstatic.com` and `style-src
   https://fonts.googleapis.com` to a policy whose whole point is
   `default-src 'none'`. If the fallback ever reads wrong enough to matter,
   self-host a woff2 in `dist/control` and relax to `font-src 'self'` — a far
   smaller concession than a third-party origin.
   ────────────────────────────────────────────────────────────────────────── */

body {
  /* ⚠️ `--mondrian-chrome`, NOT `--mondrian-bg`. The host paints our slot with
     its own chrome token (`div.gi-panel.bg-mondrian-chrome`), and the two
     genuinely diverge per theme: chrome is `var(--mondrian-bg)` under
     minimalist but `var(--mondrian-bg-darkest)` at `:root`. Reading bg here is
     what made multifamily's whole panel render as a recessed card against the
     host — ΔL* 3.12, the same step as a real card's own separation. Verified
     live 2026-09-04. Do not "simplify" this. */
  background: var(--mondrian-chrome);
  color: var(--mondrian-text);
  font-family: Inter, system-ui, sans-serif;
  font-size: 11px;
  line-height: 1.45;
  font-optical-sizing: auto;
  font-feature-settings: "calt", "liga";
  margin: 0;
  padding: 12px;
}

/* ── Header ──────────────────────────────────────────────────────────────────
   No logo and no wordmark, deliberately. This panel renders INSIDE Giraffe;
   the product's own panels carry neither, and a Giraffe logo here would be the
   app claiming to be the chrome around it.
   ────────────────────────────────────────────────────────────────────────── */

.gm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.gm-head h1 {
  font-size: 12px;
  /* Giraffe remaps `font-bold` to 550 in its own CSS rather than shipping a
     700 — a half-step, and the reason the product's hierarchy reads so quiet.
     Matching it matters more than any single colour. */
  font-weight: 550;
  line-height: 14px;
  margin: 0;
  color: var(--mondrian-text-bright);
}

/* The session state, which is the one fact worth reading before anything else
   on this panel. `live` earns the accent; the other two states are real
   answers rather than failures, so they stay neutral rather than going red. */
.gm-badge {
  font-size: 11px;
  font-weight: 550;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--mondrian-bg-dark);
  color: var(--mondrian-text-dim);
  white-space: nowrap;
}

.gm-badge[data-state="live"] {
  background: var(--mondrian-accent);
  color: var(--mondrian-on-accent);
}

/* ── The fact rows ───────────────────────────────────────────────────────── */

.gm-rows {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  align-items: baseline;
  margin: 0 0 12px;
  padding: 8px 10px;
  background: var(--mondrian-bg-lighter);
  border: 1px solid var(--mondrian-border);
  border-radius: 6px;
}

.gm-rows dt {
  color: var(--mondrian-text-dim);
}

.gm-rows dd {
  margin: 0;
  min-width: 0;
  color: var(--mondrian-text-bright);
  /* Ids are long and this panel is narrow; truncating beats reflowing the
     grid, and the full value stays reachable by selection. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A name over its id (#37). Stacked, each truncated on its own line, because a
   long workspace name on one line with its id would push the id out of sight —
   and the id is what every error message and issue quotes. */
.gm-named > * {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gm-name:empty {
  display: none;
}

/* The frame reports an id the verified token does not list. */
.gm-name[data-unlisted="true"] {
  color: var(--mondrian-danger);
}

.gm-rows code {
  /* An OS monospace stack, downloading nothing. Ids are the one place a
     proportional face genuinely costs legibility. */
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  color: var(--mondrian-text);
}

/* The socket is a STATE, not prose, so it gets a mark that can be read without
   reading. `data-socket` is written by the bridge's status handler. */
.gm-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  margin-right: 6px;
  vertical-align: baseline;
  background: var(--mondrian-text-dim);
}

[data-socket="connected"] .gm-dot {
  background: var(--mondrian-accent);
}

[data-socket="disconnected"] .gm-dot {
  background: var(--mondrian-danger);
}

/* ── Actions ─────────────────────────────────────────────────────────────────
   These two buttons were peers and are not: Connect is the step a human takes
   first and the only one that opens a popup, so it carries the accent and
   Activate carries a border. Retry is a third, hidden unless the bridge has
   stopped dialling.
   ────────────────────────────────────────────────────────────────────────── */

.gm-actions {
  display: flex;
  gap: 6px;
}

.gm-btn {
  font: inherit;
  font-weight: 550;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--mondrian-border-bright);
  background: var(--mondrian-bg-lighter);
  color: var(--mondrian-text);
  cursor: pointer;
}

.gm-btn:hover {
  background: var(--mondrian-bg-dark);
}

.gm-btn--primary {
  border-color: transparent;
  background: var(--mondrian-accent);
  color: var(--mondrian-on-accent);
}

.gm-btn--primary:hover {
  /* Derived, never fixed: the accent is configurable per workspace, so a
     hardcoded hover would disagree with whatever the user chose. */
  background: color-mix(in srgb, var(--mondrian-accent) 85%, black);
}

.gm-btn:focus-visible {
  outline: 2px solid var(--mondrian-accent);
  outline-offset: 1px;
}

/* ── Status ──────────────────────────────────────────────────────────────────
   The panel's one prose surface, and it stays empty until there is something
   to say. It carries the ACTION a user can take, never the raw message — those
   go to the console, because their content is not ours to bound.
   ────────────────────────────────────────────────────────────────────────── */

.gm-status {
  margin: 10px 0 0;
  color: var(--mondrian-text-dim);
}

.gm-status:empty {
  display: none;
}
